// =============================================================
// GovWins.jsx — Government opportunity search (DB-backed)
// =============================================================
import { useState, useEffect, useCallback, useRef } from 'react';
import { B } from '../constants/brand';
import { fmt } from '../utils/format';
import { api } from '../api/client';
import { useApi } from '../hooks/useApi';
import { Btn, Input, Select, Spinner, Card, Badge, PageHeader, EmptyState, Icon, Modal, ModalFooter, Tabs, useToast } from '../components/ui';
import { RecommendationsPanel } from '../components/govbids/RecommendationsPanel';
import { BidProfilesPanel } from '../components/govbids/BidProfilesPanel';
import { LibraryPanel } from '../components/govbids/LibraryPanel';
import { OpportunityDocsModal } from '../components/shared/OpportunityDocsModal';
import FileDropZone from '../components/shared/FileDropZone';
import { formatInstant } from '../utils/format';

// ── Constants ─────────────────────────────────────────────────
const SET_ASIDE_OPTIONS = [
  { value: '',       label: 'All Set-Asides' },
  { value: 'SBA',    label: 'Small Business' },
  { value: '8A',     label: '8(a) Program' },
  { value: 'HZC',    label: 'HUBZone' },
  { value: 'SDVOSBC',label: 'Service-Disabled Veteran (SDVOSB)' },
  { value: 'VSB',    label: 'Veteran-Owned (VOSB)' },
  { value: 'WOSB',   label: "Women-Owned (WOSB)" },
  { value: 'EDWOSB', label: "Economically Disadvantaged WOSB" },
  { value: 'RP',     label: 'R&D' },
  { value: 'SS',     label: 'Total Small Business Set-Aside' },
];

const SOURCES_OPTIONS = [
  { value: 'all',                            label: 'All Sources' },
  { value: 'sam',                            label: 'SAM.gov (Federal)' },
  { value: 'usaspending',                    label: 'USASpending.gov (Federal Awards)' },
  { value: 'bidnet',                         label: 'BidNet Direct (State/Local)' },
  { value: 'staterss',                       label: 'State RSS Feeds' },
  { value: 'webcrawl',                       label: 'Web Crawl (CA, NY, ...)' },
  { value: 'govwiniq',                       label: 'GovWin IQ (API + imports)' },
  { value: 'sam,usaspending',                label: 'Federal Only' },
  { value: 'bidnet,staterss,webcrawl',       label: 'State/Local Only' },
];

const SOURCE_COLORS = {
  'SAM.gov':      B.navy,
  'USASpending':  '#6366f1',
  'BidNet':       '#0891b2',
  'StateRSS':     '#059669',
  'WebCrawl':     '#d97706',
  'GovWin IQ':    '#7c3aed',
};

const RUN_STATUS_COLORS = {
  running:   B.amber,
  completed: B.green,
  error:     B.red,
};

const PAGE_SIZE = 25;
// Admin-only views beside the search: AI bid recommendations and the per-company bid profiles they read.
const VIEWS = ['Search', 'Recommendations', 'Bid profiles', 'Company library'];

// ── Helpers ───────────────────────────────────────────────────
const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  return Math.ceil((new Date(dateStr) - new Date()) / 86400000);
};

const DeadlinePill = ({ date }) => {
  const days = daysUntil(date);
  if (days === null) return <span style={{ color: B.textLt }}>—</span>;
  const color  = days < 0 ? B.textLt : days <= 7 ? B.red : days <= 21 ? B.amber : B.green;
  const weight = days >= 0 && days <= 7 ? 700 : 400;
  const label  = days < 0 ? `${Math.abs(days)}d ago` : days === 0 ? 'Today' : `${days}d`;
  return (
    <span style={{ color, fontWeight: weight, fontSize: 12 }}>
      {fmt.date(date)} <span style={{ opacity: 0.7 }}>({label})</span>
    </span>
  );
};

const TypeBadge = ({ type }) => {
  const colors = {
    Solicitation: B.navy, Presolicitation: B.amber,
    'Sources Sought': B.tealDk, 'Special Notice': B.textMd,
    'Combined Synopsis/Solicitation': B.navyLt,
  };
  return <Badge color={colors[type] || B.textMd}>{type}</Badge>;
};

const SourceBadge = ({ source }) => (
  <Badge color={SOURCE_COLORS[source] || B.textMd} style={{ fontSize: 10 }}>{source}</Badge>
);

// ── Main Component ────────────────────────────────────────────
export const GovWins = ({ user }) => {
  const isAdmin = user?.role === 'Admin';
  const { toast } = useToast();

  // Documents modal (per opportunity) + GovWin IQ import modal
  const [docsFor, setDocsFor] = useState(null);
  const [view, setView] = useState('Search');
  const [showImport, setShowImport] = useState(false);

  // Search form
  const [form, setForm] = useState({
    keyword: '', naicsCode: '', setAside: '',
    postedFrom: '', postedTo: '', activeOnly: true, sources: 'all',
  });
  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // Search results
  const [results,  setResults]  = useState(null);
  const [total,    setTotal]    = useState(0);
  const [offset,   setOffset]   = useState(0);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);
  const [selected, setSelected] = useState(null);
  const [lastRun,  setLastRun]  = useState(null);
  // COUNT(*) over the whole store, from the search endpoint. Deliberately not
  // lastRun.totalFound: that is the last SCRAPER run's row count, and a GovWin
  // IQ import writes no run row, so it read "0 opportunities" over a store
  // holding every imported bid.
  const [storeCount, setStoreCount] = useState(null);

  // Scheduler state
  const [runningJob,   setRunningJob]   = useState(false);
  const [runJobError,  setRunJobError]  = useState(null);
  const [showHistory,  setShowHistory]  = useState(false);
  const pollRef = useRef(null);

  // Keyword management
  const [showKeywords, setShowKeywords] = useState(false);
  const [kwForm, setKwForm] = useState({ keyword: '', naicsCode: '', sources: 'all' });
  const [kwSaving, setKwSaving] = useState(false);
  const [kwError,  setKwError]  = useState(null);

  const { data: keywords, reload: reloadKeywords } = useApi(
    () => api.get('govwins/keywords', { timeout: 30000 }),
    []
  );

  const { data: runsData, reload: reloadRuns } = useApi(
    () => api.get('govwins/runs', { timeout: 30000 }),
    []
  );

  const runs = runsData || [];

  // ── Search ─────────────────────────────────────────────────
  const search = useCallback(async (newOffset = 0) => {
    setLoading(true);
    setError(null);
    setSelected(null);
    try {
      const params = new URLSearchParams({
        limit:      PAGE_SIZE,
        offset:     newOffset,
        activeOnly: form.activeOnly,
      });
      if (form.keyword)    params.set('keyword',    form.keyword.trim());
      if (form.naicsCode)  params.set('naicsCode',  form.naicsCode.trim());
      if (form.setAside)   params.set('setAside',   form.setAside);
      if (form.postedFrom) params.set('postedFrom', form.postedFrom);
      if (form.postedTo)   params.set('postedTo',   form.postedTo);
      if (form.sources && form.sources !== 'all') params.set('sources', form.sources);

      const data = await api.get(`govwins/search?${params.toString()}`, { timeout: 30000 });
      setResults(data.opportunities || []);
      setTotal(data.total || 0);
      setOffset(newOffset);
      if (data.lastRun) setLastRun(data.lastRun);
      if (data.storeCount != null) setStoreCount(data.storeCount);
    } catch (e) {
      setError(e.message);
      setResults(null);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const handleSubmit = (e) => {
    e?.preventDefault();
    search(0);
  };

  // Auto-load the list on first open so opportunities (including imported
  // GovWin IQ rows) are visible without requiring an explicit Search click.
  useEffect(() => { search(0); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Run Now ────────────────────────────────────────────────
  const startPoll = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const freshRuns = await api.get('govwins/runs');
        reloadRuns();
        const latest = (freshRuns || [])[0];
        if (latest && latest.status !== 'running') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setRunningJob(false);
          if (latest.completedUTC) {
            setLastRun({ completedUTC: latest.completedUTC, totalFound: latest.totalFound });
          }
        }
      } catch (_) { /* ignore poll errors */ }
    }, 4000);
  }, [reloadRuns]);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const runNow = async () => {
    setRunningJob(true);
    setRunJobError(null);
    try {
      await api.post('govwins/run', {});
      reloadRuns();
      startPoll();
    } catch (e) {
      setRunJobError(e.message);
      setRunningJob(false);
    }
  };

  // ── Keyword management ─────────────────────────────────────
  const addKeyword = async () => {
    if (!kwForm.keyword.trim() && !kwForm.naicsCode.trim()) {
      setKwError('Keyword or NAICS code is required');
      return;
    }
    setKwSaving(true);
    setKwError(null);
    try {
      await api.post('govwins/keywords', {
        keyword:   kwForm.keyword.trim() || null,
        naicsCode: kwForm.naicsCode.trim() || null,
        sources:   kwForm.sources,
      });
      setKwForm({ keyword: '', naicsCode: '', sources: 'all' });
      reloadKeywords();
    } catch (e) {
      setKwError(e.message);
    } finally {
      setKwSaving(false);
    }
  };

  const deleteKeyword = async (id) => {
    try {
      await api.del(`govwins/keywords/${id}`);
      reloadKeywords();
    } catch (_) { /* ignore */ }
  };

  // ── Derived ────────────────────────────────────────────────
  const totalPages  = Math.ceil(total / PAGE_SIZE);
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  const viewTabs = isAdmin ? <Tabs tabs={VIEWS} active={view} onChange={setView} /> : null;
  if (isAdmin && view !== 'Search') {
    return (
      <div>
        <PageHeader icon="govwins" title="Government Opportunities"
          subtitle={view === 'Recommendations'
            ? 'Opportunities the AI screen graded at or above the bar for one of your bidding companies, from the notice description.'
            : view === 'Company library'
              ? 'Past contracts and company material each bidding company can draw on — confirmed contracts feed the bid screen.'
              : 'What each bidding company can win — read by the rules filter and the AI screen.'} />
        {viewTabs}
        {view === 'Recommendations' ? <RecommendationsPanel onOpenDocs={setDocsFor} />
          : view === 'Company library' ? <LibraryPanel /> : <BidProfilesPanel />}
        {docsFor && (
          <OpportunityDocsModal opportunity={docsFor} isAdmin={isAdmin} onClose={() => setDocsFor(null)} onChanged={() => {}} />
        )}
      </div>
    );
  }

  return (
    <div>
      {/* ── Header ─────────────────────────────────────────── */}
      <PageHeader
        icon="govwins"
        title="Government Opportunities"
        subtitle={
          <>
            Opportunities stored from daily scheduled runs across SAM.gov, USASpending, BidNet, state RSS feeds, and more, plus GovWin IQ imports (email &amp; spreadsheet)
            {storeCount != null && (
              <span style={{ marginLeft: 12, color: B.textMd }}>
                · {storeCount.toLocaleString()} opportunities stored
              </span>
            )}
            {lastRun?.completedUTC && (
              <span style={{ marginLeft: 12, color: B.textMd }}>
                · Last scraper run {fmt.date(lastRun.completedUTC)}
                {lastRun.totalFound != null && ` (${lastRun.totalFound.toLocaleString()} found)`}
              </span>
            )}
          </>
        }
        actions={
          <>
            <Btn size="sm" variant="ghost" onClick={() => setShowHistory(v => !v)}>
              {showHistory ? 'Hide History' : 'Run History'}
            </Btn>
            {isAdmin && (
              <Btn size="sm" variant="ghost" onClick={() => setShowImport(true)}>
                <Icon name="upload" size={14} /> Import GovWin export
              </Btn>
            )}
            <Btn size="sm" onClick={runNow} disabled={runningJob}>
              {runningJob ? 'Running…' : <><Icon name="play" size={14} /> Run Now</>}
            </Btn>
          </>
        }
      />
      {viewTabs}

      {/* ── Run Job Error ───────────────────────────────────── */}
      {runJobError && (
        <div style={{ background: B.red + '10', border: `1px solid ${B.red}33`, borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 12, color: B.red }}>
          Failed to start job: {runJobError}
        </div>
      )}

      {/* ── Run History ─────────────────────────────────────── */}
      {showHistory && (
        <Card style={{ marginBottom: 20 }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: B.text, marginBottom: 12 }}>Run History</div>
          {runs.length === 0 ? (
            <div style={{ fontSize: 13, color: B.textLt }}>No runs yet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {runs.slice(0, 10).map(run => (
                <div key={run.runId} style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '8px 10px', background: B.surfaceMuted,
                  borderRadius: 6, fontSize: 12,
                }}>
                  <Badge color={RUN_STATUS_COLORS[run.status] || B.textMd} style={{ minWidth: 72, textAlign: 'center' }}>
                    {run.status}
                  </Badge>
                  <span style={{ color: B.textMd, minWidth: 140 }}>
                    {run.startedUTC ? fmt.date(run.startedUTC) : '—'}
                    {run.startedUTC && ` ${formatInstant(run.startedUTC, { hour: '2-digit', minute: '2-digit' })}`}
                  </span>
                  <span style={{ color: B.text, fontWeight: 600 }}>
                    {run.totalFound != null ? `${run.totalFound.toLocaleString()} found` : '—'}
                  </span>
                  {run.sourceResults && Object.keys(run.sourceResults).length > 0 && (
                    <span style={{ color: B.textLt, flex: 1 }}>
                      {Object.entries(run.sourceResults)
                        .filter(([, n]) => n > 0)
                        .map(([src, n]) => `${src}: ${n}`)
                        .join(' · ')}
                    </span>
                  )}
                  <span style={{ color: B.textLt, marginLeft: 'auto' }}>
                    {run.triggeredBy === 'manual' ? 'Manual' : 'Scheduled'}
                  </span>
                  {run.errorMessage && (
                    <span style={{ color: B.red, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {run.errorMessage}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* ── Search Form ─────────────────────────────────────── */}
      <Card style={{ marginBottom: 20 }}>
        <form onSubmit={handleSubmit}>
          <div className="harvus-responsive-grid" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
            <Input
              label="Keyword"
              value={form.keyword}
              onChange={v => setF('keyword', v)}
              placeholder="Title, agency, NAICS, solicitation number or Opp ID (e.g. RM-203652)"
            />
            <Input
              label="NAICS Code"
              value={form.naicsCode}
              onChange={v => setF('naicsCode', v)}
              placeholder="e.g. 541511"
            />
            <Select label="Set-Aside" value={form.setAside} onChange={v => setF('setAside', v)}
              options={SET_ASIDE_OPTIONS} />
          </div>
          <div className="harvus-responsive-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr auto', gap: 12, alignItems: 'flex-end' }}>
            <Select label="Source" value={form.sources} onChange={v => setF('sources', v)}
              options={SOURCES_OPTIONS} />
            <Input label="Posted From" type="date" value={form.postedFrom} onChange={v => setF('postedFrom', v)} />
            <Input label="Posted To"   type="date" value={form.postedTo}   onChange={v => setF('postedTo', v)} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingBottom: 2 }}>
              <input
                type="checkbox"
                id="activeOnly"
                checked={form.activeOnly}
                onChange={e => setF('activeOnly', e.target.checked)}
                style={{ width: 16, height: 16, cursor: 'pointer' }}
              />
              <label htmlFor="activeOnly" style={{ fontSize: 13, color: B.textMd, cursor: 'pointer', userSelect: 'none' }}>
                Active only
              </label>
            </div>
            <Btn type="submit" disabled={loading}>
              {loading ? 'Searching…' : 'Search'}
            </Btn>
          </div>
        </form>
      </Card>

      {/* ── Error ───────────────────────────────────────────── */}
      {error && (
        <div style={{ background: B.red + '10', border: `1px solid ${B.red}33`, borderRadius: 8, padding: '12px 16px', marginBottom: 16, color: B.red, fontSize: 13 }}>
          {error}
        </div>
      )}

      {/* ── Loading ─────────────────────────────────────────── */}
      {loading && <div style={{ textAlign: 'center', padding: 60 }}><Spinner /></div>}

      {/* ── Results ─────────────────────────────────────────── */}
      {!loading && results && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ fontSize: 13, color: B.textMd }}>
              {total === 0
                ? 'No results found.'
                : `${total.toLocaleString()} result${total !== 1 ? 's' : ''}`
              }
            </div>
            {totalPages > 1 && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
                <Btn size="sm" variant="ghost" disabled={currentPage === 1} onClick={() => search(offset - PAGE_SIZE)}><Icon name="arrowLeft" size={14} /> Prev</Btn>
                <span style={{ color: B.textMd }}>Page {currentPage} of {totalPages}</span>
                <Btn size="sm" variant="ghost" disabled={currentPage === totalPages} onClick={() => search(offset + PAGE_SIZE)}>Next <Icon name="arrowRight" size={14} /></Btn>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {results.map(opp => (
              <Card key={opp.noticeId} style={{
                cursor: 'pointer',
                borderLeft: selected?.noticeId === opp.noticeId ? `4px solid ${B.navy}` : `4px solid transparent`,
                transition: 'border-color 0.15s',
              }}
                onClick={() => setSelected(selected?.noticeId === opp.noticeId ? null : opp)}>

                {/* ── Card Header ──── */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 15, color: B.text, marginBottom: 4 }}>{opp.title}</div>
                    <div style={{ fontSize: 12, color: B.textLt, marginBottom: 8 }}>
                      {opp.solicitationNumber && <span style={{ marginRight: 16 }}>#{opp.solicitationNumber}</span>}
                      {opp.agency && <span style={{ marginRight: 16 }}>{opp.agency}</span>}
                      {opp.subAgency && <span style={{ color: B.textLt }}>{opp.subAgency}</span>}
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                      {opp.sourceSystem && <SourceBadge source={opp.sourceSystem} />}
                      {opp.type && <TypeBadge type={opp.type} />}
                      {opp.setAsideType && <Badge color={B.tealDk}>{opp.setAsideType}</Badge>}
                      {opp.naicsCode && <Badge color={B.textLt}>{opp.naicsCode}{opp.naicsDescription ? ` · ${opp.naicsDescription}` : ''}</Badge>}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0, minWidth: 140 }}>
                    <div style={{ fontSize: 11, color: B.textLt, marginBottom: 4 }}>Response Deadline</div>
                    <DeadlinePill date={opp.responseDeadline} />
                    <div style={{ fontSize: 11, color: B.textLt, marginTop: 8 }}>Posted</div>
                    <div style={{ fontSize: 12, color: B.textMd }}>{fmt.date(opp.postedDate)}</div>
                    {opp.runCount > 1 && (
                      <div style={{ fontSize: 10, color: B.textLt, marginTop: 4 }}>seen {opp.runCount}× runs</div>
                    )}
                    {opp.opportunityId && (
                      <Btn size="sm" variant="ghost"
                        onClick={e => { e.stopPropagation(); setDocsFor(opp); }}
                        style={{ marginTop: 8, padding: '2px 8px' }}>
                        <Icon name="file" size={13} /> Documents{opp.docCount ? ` (${opp.docCount})` : ''}
                      </Btn>
                    )}
                  </div>
                </div>

                {/* ── Expanded Detail ──── */}
                {selected?.noticeId === opp.noticeId && (
                  <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${B.border}` }}>
                    {opp.description && (
                      <div style={{ marginBottom: 16 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: B.textLt, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Description</div>
                        <div style={{ fontSize: 13, color: B.textMd, lineHeight: 1.6, whiteSpace: 'pre-wrap', maxHeight: 200, overflowY: 'auto' }}>
                          {opp.description.replace(/<[^>]+>/g, '').trim() || '—'}
                        </div>
                      </div>
                    )}

                    {opp.pointOfContact?.length > 0 && (
                      <div style={{ marginBottom: 16 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: B.textLt, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Point of Contact</div>
                        {opp.pointOfContact.map((poc, i) => (
                          <div key={i} style={{ fontSize: 13, color: B.textMd }}>
                            {poc.name && <span style={{ fontWeight: 600 }}>{poc.name}</span>}
                            {poc.email && <span> · <a href={`mailto:${poc.email}`} style={{ color: B.navy }}>{poc.email}</a></span>}
                            {poc.phone && <span> · {poc.phone}</span>}
                          </div>
                        ))}
                      </div>
                    )}

                    {opp.award && (
                      <div style={{ marginBottom: 16 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: B.textLt, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Award</div>
                        <div style={{ fontSize: 13, color: B.textMd }}>
                          {opp.award.awardee && <span style={{ fontWeight: 600 }}>{opp.award.awardee}</span>}
                          {opp.award.amount  && <span> · {fmt.currency(opp.award.amount)}</span>}
                          {opp.award.date    && <span> · {fmt.date(opp.award.date)}</span>}
                        </div>
                      </div>
                    )}

                    {opp.uiLink && (
                      <a
                        href={opp.uiLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={e => e.stopPropagation()}
                        style={{
                          display: 'inline-block', padding: '8px 16px',
                          background: SOURCE_COLORS[opp.sourceSystem] || B.navy, color: '#fff',
                          borderRadius: 6, fontSize: 13, fontWeight: 600,
                          textDecoration: 'none',
                        }}>
                        View on {opp.sourceSystem || 'Source'} <Icon name="external" size={13} style={{ display: 'inline-block', verticalAlign: 'text-bottom' }} />
                      </a>
                    )}
                  </div>
                )}
              </Card>
            ))}
          </div>

          {/* ── Bottom Pagination ── */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 20 }}>
              <Btn size="sm" variant="ghost" disabled={currentPage === 1} onClick={() => search(offset - PAGE_SIZE)}><Icon name="arrowLeft" size={14} /> Prev</Btn>
              <span style={{ padding: '6px 12px', fontSize: 13, color: B.textMd }}>Page {currentPage} of {totalPages}</span>
              <Btn size="sm" variant="ghost" disabled={currentPage === totalPages} onClick={() => search(offset + PAGE_SIZE)}>Next <Icon name="arrowRight" size={14} /></Btn>
            </div>
          )}
        </>
      )}

      {/* ── Empty prompt ─────────────────────────────────────── */}
      {!loading && !results && !error && (
        <EmptyState
          icon="govwins"
          title="Search Stored Opportunities"
          message="Opportunities are collected daily from configured search terms and from GovWin IQ imports. Use the filters above to search the database, or click Run Now to fetch the latest."
          action={
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
              {Object.entries(SOURCE_COLORS).map(([src, color]) => (
                <Badge key={src} color={color}>{src}</Badge>
              ))}
            </div>
          }
        />
      )}

      {/* ── Manage Search Terms ──────────────────────────────── */}
      <div style={{ marginTop: 32 }}>
        <button
          onClick={() => setShowKeywords(v => !v)}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 13, fontWeight: 600, color: B.textMd,
            display: 'flex', alignItems: 'center', gap: 6, padding: 0,
          }}>
          <Icon name={showKeywords ? 'chevronDown' : 'chevronRight'} size={13} />
          Manage Search Terms
          <span style={{ fontWeight: 400, color: B.textLt }}>
            ({Array.isArray(keywords) ? keywords.filter(k => k.isActive).length : '…'} active)
          </span>
        </button>

        {showKeywords && (
          <Card style={{ marginTop: 10 }}>
            {/* Existing keywords */}
            {Array.isArray(keywords) && keywords.length > 0 ? (
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {keywords.map(kw => (
                    <div key={kw.keywordID} style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '6px 10px', background: B.surfaceMuted, borderRadius: 6, fontSize: 13,
                    }}>
                      {kw.keyword && <span style={{ fontWeight: 600, color: B.text }}>{kw.keyword}</span>}
                      {kw.naicsCode && <Badge color={B.textLt}>{kw.naicsCode}</Badge>}
                      {kw.setAside && <Badge color={B.tealDk}>{kw.setAside}</Badge>}
                      <Badge color={SOURCE_COLORS[kw.sources] || B.textMd} style={{ fontSize: 10 }}>
                        {kw.sources === 'all' ? 'All Sources' : kw.sources}
                      </Badge>
                      <span style={{ marginLeft: 'auto' }}>
                        <Btn size="sm" variant="ghost" onClick={() => deleteKeyword(kw.keywordID)}
                          style={{ color: B.red, padding: '2px 8px' }}>
                          Remove
                        </Btn>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div style={{ fontSize: 13, color: B.textLt, marginBottom: 16 }}>No search terms configured. Add one below to start collecting opportunities.</div>
            )}

            {/* Add keyword form */}
            <div style={{ borderTop: `1px solid ${B.border}`, paddingTop: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: B.textLt, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>Add Search Term</div>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: 10, alignItems: 'flex-end' }}>
                <Input
                  label="Keyword"
                  value={kwForm.keyword}
                  onChange={v => setKwForm(p => ({ ...p, keyword: v }))}
                  placeholder="e.g. cybersecurity"
                />
                <Input
                  label="NAICS Code"
                  value={kwForm.naicsCode}
                  onChange={v => setKwForm(p => ({ ...p, naicsCode: v }))}
                  placeholder="e.g. 541511"
                />
                <Select
                  label="Sources"
                  value={kwForm.sources}
                  onChange={v => setKwForm(p => ({ ...p, sources: v }))}
                  options={SOURCES_OPTIONS}
                />
                <Btn onClick={addKeyword} disabled={kwSaving}>
                  {kwSaving ? 'Adding…' : 'Add'}
                </Btn>
              </div>
              {kwError && (
                <div style={{ fontSize: 12, color: B.red, marginTop: 8 }}>{kwError}</div>
              )}
            </div>
          </Card>
        )}
      </div>

      {/* ── Documents modal ──────────────────────────────────── */}
      {docsFor && (
        <OpportunityDocsModal
          opportunity={docsFor}
          isAdmin={isAdmin}
          onClose={() => setDocsFor(null)}
          onChanged={() => search(offset)}
        />
      )}

      {/* ── GovWin IQ import modal ───────────────────────────── */}
      {showImport && (
        <GovWinImportModal
          toast={toast}
          onClose={() => setShowImport(false)}
          onDone={() => { setShowImport(false); search(0); }}
        />
      )}
    </div>
  );
};

// ── GovWin IQ .xlsx import modal ──────────────────────────────
const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

function GovWinImportModal({ onClose, onDone, toast }) {
  const [file, setFile]     = useState(null);
  const [busy, setBusy]     = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr]       = useState(null);

  const run = async () => {
    if (!file) { setErr('Choose an .xlsx file to import'); return; }
    setBusy(true); setErr(null); setResult(null);
    try {
      const { importId, sasUrl } = await api.post('govwins/import', { fileName: file.name });
      const put = await fetch(sasUrl, {
        method: 'PUT',
        headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': XLSX_MIME },
        body: file,
      });
      if (!put.ok) throw new Error(`Upload failed (${put.status})`);
      const res = await api.post(`govwins/import/${importId}/process`, {});
      setResult(res);
      toast?.success?.(`Imported ${res.upserted} bid(s)`);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Import GovWin IQ export" onClose={onClose} width={520}>
      <div style={{ fontSize: 13, color: B.textMd, lineHeight: 1.5, marginBottom: 14 }}>
        Upload a GovWin IQ “Opportunity Search Results” export (<b>.xlsx</b>). Bids are added to
        Gov Opportunities tagged as a <b>GovWin IQ</b> source. For active federal bids with a
        solicitation number, documents are pulled from SAM.gov automatically — you can also pull or
        attach files per bid from its Documents panel.
      </div>
      <FileDropZone accept={['.xlsx']} multiple={false} disabled={busy} label="Drop the .xlsx export"
        onFiles={fs => { setFile(fs[0]); setResult(null); setErr(null); }}
        onRejected={() => setErr('Only a GovWin .xlsx export can be imported.')}>
        <div style={{ border: `1px dashed ${B.border}`, borderRadius: 10, padding: 14, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <input
            type="file"
            accept=".xlsx"
            onChange={e => { setFile(e.target.files?.[0] || null); setResult(null); setErr(null); }}
            style={{ fontSize: 13 }}
          />
          <span style={{ fontSize: 12, color: B.textLt }}>{file ? file.name : 'or drag the .xlsx here'}</span>
        </div>
      </FileDropZone>
      {err && <div style={{ color: B.red, fontSize: 12, marginTop: 10 }}>{err}</div>}
      {result && (
        <div style={{ marginTop: 14, fontSize: 13, color: B.text, background: B.surfaceMuted, borderRadius: 6, padding: '10px 12px', lineHeight: 1.5 }}>
          Imported <b>{result.upserted}</b> of {result.rowsTotal} bid(s)
          {result.skipped ? `, ${result.skipped} skipped` : ''}.
          {result.docsPulled ? <> Pulled documents for <b>{result.docsPulled}</b> bid(s).</> : null}
          {result.docPullCapped ? <> {result.docPullCapped} more active bid(s) can be pulled on demand from their Documents panel.</> : null}
        </div>
      )}
      <ModalFooter>
        <Btn variant="ghost" onClick={onClose} disabled={busy}>{result ? 'Close' : 'Cancel'}</Btn>
        {!result && <Btn onClick={run} disabled={busy || !file}>{busy ? 'Importing…' : 'Upload & import'}</Btn>}
        {result && <Btn onClick={onDone}>Done</Btn>}
      </ModalFooter>
    </Modal>
  );
}
